/**
 * 3D COCKPIT's **port-side** half of the airframe/dashboard split: which
 * aeroplanes phase 1 actually engages for.
 *
 * The split itself — the block spans, and the rule that turns them into "which
 * ops are the aeroplane" — is a fact about the original's own cockpit scripts,
 * so it lives with the disassembly that established it
 * (`packages/baker/src/cockpit-airframe.ts`, beside `cockpit-bake.ts`'s block
 * table) and is re-exported here for the panel's benefit. What is *not* a fact
 * about the scripts is the list below: it is a record of which aeroplanes a
 * human has looked at, and that belongs to the port.
 */

import { FORWARD_BLOCK_SPANS } from "@red-opus/baker/schema";

export { airframeOpIndices, dashFamilyOps, FORWARD_BLOCK_SPANS, type ForwardBlockSpans } from "@red-opus/baker/schema";

/**
 * The aeroplanes 3D COCKPIT actually engages for: **all 22 flyable scouts**, and
 * the list is no longer written down here at all.
 *
 * Phase 1 shipped the Camel alone with the reason stated as a review backlog —
 * "`FORWARD_BLOCK_SPANS` is mechanical, but deciding that a given aeroplane's
 * block-1 polygons really are structure the `.glb` replaces is a look at a
 * picture, and 21 of those looks have not happened". Phase 2 is where they
 * happened, and the review found nothing per-aircraft to record. What it found
 * instead is that the *classification* is the thing that had to be checked, and
 * it is checked mechanically for the whole roster rather than by eye:
 *
 * - **the guns and the sight are never airframe**, on any of the 22, idle or
 *   firing (`tools/bake/test/cockpit-airframe.test.ts`'s invariant) — so no
 *   aeroplane can lose its painted guns to a bad span pair;
 * - **the dash quad projects back onto the logical pixels it was unprojected
 *   from**, per airframe, for every one of the 22 (`cockpit-dash-quad.test.ts`)
 *   — so no aeroplane's dashboard moves when the setting is turned on;
 * - **every one of the 22 has a measured eye offset and an enhanced `.glb`**
 *   (`cockpit-view.test.ts`, `data/enhanced/manifest.json`), which is what the
 *   two remaining preconditions of the feature are.
 *
 * Deriving the set from `FORWARD_BLOCK_SPANS` rather than transcribing it is
 * the point: an aeroplane with no span pair classifies *nothing* as airframe
 * (`airframeOpIndices`), so listing it here would turn the setting on for a
 * cockpit whose struts are still painted — the model and the painting both. The
 * two facts are one fact, so they are one table.
 */
export const COCKPIT_3D_AIRCRAFT: ReadonlySet<string> = new Set(Object.keys(FORWARD_BLOCK_SPANS));

/** Whether 3D COCKPIT draws this airframe, given the setting is on and ENHANCED MODELS is available. */
export function cockpit3dModelled(aircraftKey: string | undefined): boolean {
  return aircraftKey !== undefined && COCKPIT_3D_AIRCRAFT.has(aircraftKey.toUpperCase());
}
